import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { usePersistentFilters } from '../lib/persistentFilters';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, Filter, BarChart3, TrendingUp, Package, Truck } from 'lucide-react';
import { formatNumber, formatDate } from '../lib/utils';

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [subqualities, setSubqualities] = useState([]);
  const [lrData, setLrData] = useState([]);
  const [filters, setFilters] = usePersistentFilters('reports.filters', {
    dateFrom: '',
    dateTo: '',
    supplierId: 'all',
    qualityId: 'all',
    subqualityId: 'all',
    item: 'all',
    status: 'all'
  });

  // Get unique items from orders
  const uniqueItems = [...new Set(orders.map(o => o.item).filter(Boolean))];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, suppliersRes, qualitiesRes, subqualitiesRes, lrRes] = await Promise.allSettled([
        api.get('/grey-orders'),
        api.get('/suppliers'),
        api.get('/qualities'),
        api.get('/subqualities'),
        api.get('/reports/lr-wise')
      ]);
      setOrders(ordersRes.status === 'fulfilled' ? ordersRes.value.data : []);
      setSuppliers(suppliersRes.status === 'fulfilled' ? suppliersRes.value.data : []);
      setQualities(qualitiesRes.status === 'fulfilled' ? qualitiesRes.value.data : []);
      setSubqualities(subqualitiesRes.status === 'fulfilled' ? subqualitiesRes.value.data : []);
      setLrData(lrRes.status === 'fulfilled' ? lrRes.value.data : []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load report data');
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filters.supplierId !== 'all' && order.supplier_id !== filters.supplierId) return false;
    if (filters.qualityId !== 'all' && order.quality_id !== filters.qualityId) return false;
    if (filters.subqualityId !== 'all' && order.subquality_id !== filters.subqualityId) return false;
    if (filters.item !== 'all' && order.item !== filters.item) return false;
    if (filters.status !== 'all' && order.order_status !== filters.status) return false;
    if (filters.dateFrom && order.order_date < filters.dateFrom) return false;
    if (filters.dateTo && order.order_date > filters.dateTo) return false;
    return true;
  });

  // Calculate summary statistics
  const summary = {
    totalOrders: filteredOrders.length,
    totalOrderedQty: filteredOrders.reduce((sum, o) => sum + o.ordered_quantity, 0),
    totalBilledQty: filteredOrders.reduce((sum, o) => sum + o.total_billed_quantity, 0),
    totalReceivedQty: filteredOrders.reduce((sum, o) => sum + o.total_received_quantity, 0),
    totalValue: filteredOrders.reduce((sum, o) => sum + (o.order_amount || 0), 0),
    pendingOrders: filteredOrders.filter(o => o.order_status === 'Pending').length,
    completedOrders: filteredOrders.filter(o => o.order_status === 'Completed').length
  };

  // Group by supplier
  const supplierSummary = suppliers.map(supplier => {
    const supplierOrders = filteredOrders.filter(o => o.supplier_id === supplier.supplier_id);
    return {
      name: supplier.name,
      city: supplier.city,
      orderCount: supplierOrders.length,
      totalOrdered: supplierOrders.reduce((sum, o) => sum + o.ordered_quantity, 0),
      totalReceived: supplierOrders.reduce((sum, o) => sum + o.total_received_quantity, 0),
      totalValue: supplierOrders.reduce((sum, o) => sum + (o.order_amount || 0), 0)
    };
  }).filter(s => s.orderCount > 0).sort((a, b) => b.totalValue - a.totalValue);

  // Group by quality
  const qualitySummary = qualities.map(quality => {
    const qualityOrders = filteredOrders.filter(o => o.quality_id === quality.quality_id);
    return {
      name: quality.quality_name,
      unit: quality.default_unit,
      orderCount: qualityOrders.length,
      totalOrdered: qualityOrders.reduce((sum, o) => sum + o.ordered_quantity, 0),
      totalReceived: qualityOrders.reduce((sum, o) => sum + o.total_received_quantity, 0)
    };
  }).filter(q => q.orderCount > 0).sort((a, b) => b.totalOrdered - a.totalOrdered);

  // Group by finish quality (subquality)
  const finishQualitySummary = subqualities.map(sq => {
    const sqOrders = filteredOrders.filter(o => o.subquality_id === sq.subquality_id);
    const parentIds = sq.quality_ids?.length ? sq.quality_ids : (sq.quality_id ? [sq.quality_id] : []);
    const parentNames = parentIds
      .map(id => qualities.find(q => q.quality_id === id)?.quality_name)
      .filter(Boolean);
    return {
      name: sq.subquality_name,
      qualityName: parentNames.join(', '),
      orderCount: sqOrders.length,
      totalOrdered: sqOrders.reduce((sum, o) => sum + o.ordered_quantity, 0),
      totalReceived: sqOrders.reduce((sum, o) => sum + o.total_received_quantity, 0),
      totalValue: sqOrders.reduce((sum, o) => sum + (o.order_amount || 0), 0)
    };
  }).filter(sq => sq.orderCount > 0).sort((a, b) => b.totalOrdered - a.totalOrdered);

  // Group by item
  const itemSummary = uniqueItems.map(item => {
    const itemOrders = filteredOrders.filter(o => o.item === item);
    return {
      name: item,
      orderCount: itemOrders.length,
      totalOrdered: itemOrders.reduce((sum, o) => sum + o.ordered_quantity, 0),
      totalReceived: itemOrders.reduce((sum, o) => sum + o.total_received_quantity, 0),
      totalValue: itemOrders.reduce((sum, o) => sum + (o.order_amount || 0), 0)
    };
  }).filter(i => i.orderCount > 0).sort((a, b) => b.totalValue - a.totalValue);

  const handleExportExcel = async () => {
    try {
      toast.info('Generating Excel report...');
      const response = await api.get('/reports/grey-orders-export?format=excel', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `grey_orders_report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel report downloaded!');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const handleExportPDF = () => {
    toast.info('Generating PDF report...');
    
    // Create printable HTML content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Grey Orders Report - ${new Date().toLocaleDateString()}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1e293b; margin-bottom: 10px; }
          h2 { color: #475569; margin-top: 30px; }
          .subtitle { color: #64748b; margin-bottom: 20px; }
          .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
          .summary-card { background: #f8fafc; padding: 15px; border-radius: 8px; border: 1px solid #e2e8f0; }
          .summary-card h3 { font-size: 12px; color: #64748b; margin: 0; }
          .summary-card p { font-size: 24px; font-weight: bold; color: #1e293b; margin: 5px 0 0 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th, td { padding: 10px; text-align: left; border-bottom: 1px solid #e2e8f0; }
          th { background: #f8fafc; color: #475569; font-weight: 600; }
          td { color: #334155; }
          .text-right { text-align: right; }
          .status { padding: 4px 8px; border-radius: 4px; font-size: 12px; }
          .status-pending { background: #fef3c7; color: #92400e; }
          .status-partial { background: #fef9c3; color: #854d0e; }
          .status-completed { background: #d1fae5; color: #065f46; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Grey Orders Report</h1>
        <p class="subtitle">Generated on ${new Date().toLocaleString()}</p>
        
        <div class="summary-grid">
          <div class="summary-card">
            <h3>Total Orders</h3>
            <p>${summary.totalOrders}</p>
          </div>
          <div class="summary-card">
            <h3>Total Value</h3>
            <p>₹${formatNumber(summary.totalValue)}</p>
          </div>
          <div class="summary-card">
            <h3>Pending</h3>
            <p>${summary.pendingOrders}</p>
          </div>
          <div class="summary-card">
            <h3>Completed</h3>
            <p>${summary.completedOrders}</p>
          </div>
        </div>
        
        <h2>Orders Summary</h2>
        <table>
          <thead>
            <tr>
              <th>Order #</th>
              <th>Date</th>
              <th>Supplier</th>
              <th>Quality</th>
              <th>Finish Quality</th>
              <th>Item</th>
              <th class="text-right">Ordered</th>
              <th class="text-right">Received</th>
              <th class="text-right">Value</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${filteredOrders.slice(0, 100).map(order => `
              <tr>
                <td>${order.order_number}</td>
                <td>${formatDate(order.order_date)}</td>
                <td>${order.supplier_name}</td>
                <td>${order.quality_name}</td>
                <td>${order.subquality_name || '-'}</td>
                <td>${order.item || '-'}</td>
                <td class="text-right">${formatNumber(order.ordered_quantity)} ${order.order_unit}</td>
                <td class="text-right">${formatNumber(order.total_received_quantity)} ${order.order_unit}</td>
                <td class="text-right">₹${formatNumber(order.order_amount || 0)}</td>
                <td><span class="status status-${order.order_status.toLowerCase().replace(' ', '-')}">${order.order_status}</span></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        ${filteredOrders.length > 100 ? `<p style="color: #64748b; margin-top: 10px;">Showing 100 of ${filteredOrders.length} orders</p>` : ''}
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const doc = printWindow.document;
      doc.open();
      doc.close();
      while (doc.head.firstChild) doc.head.removeChild(doc.head.firstChild);
      while (doc.body.firstChild) doc.body.removeChild(doc.body.firstChild);
      const iframe = doc.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;border:none;';
      iframe.srcdoc = printContent;
      doc.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.print();
        toast.success('PDF ready for download!');
      };
    }
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      supplierId: 'all',
      qualityId: 'all',
      subqualityId: 'all',
      item: 'all',
      status: 'all'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="reports-page" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Reports
            </h1>
            <p className="text-slate-600 mt-1">Analyze orders, suppliers, and quality data</p>
          </div>
          <div className="flex gap-2">
            <Button 
              data-testid="export-pdf-btn"
              variant="outline"
              onClick={handleExportPDF}
            >
              <FileText className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
            <Button 
              data-testid="export-excel-btn"
              onClick={handleExportExcel} 
              className="bg-teal-600 hover:bg-teal-700"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6 border border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>From Date</Label>
              <Input
                type="date"
                data-testid="filter-date-from"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="h-10"
              />
            </div>
            <div>
              <Label>To Date</Label>
              <Input
                type="date"
                data-testid="filter-date-to"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="h-10"
              />
            </div>
            <div>
              <Label>Supplier</Label>
              <Select value={filters.supplierId} onValueChange={(v) => setFilters({ ...filters, supplierId: v })}>
                <SelectTrigger data-testid="filter-supplier" className="h-10">
                  <SelectValue placeholder="All Suppliers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Suppliers</SelectItem>
                  {suppliers.map(s => (
                    <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quality</Label>
              <Select value={filters.qualityId} onValueChange={(v) => setFilters({ ...filters, qualityId: v })}>
                <SelectTrigger data-testid="filter-quality" className="h-10">
                  <SelectValue placeholder="All Qualities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Qualities</SelectItem>
                  {qualities.map(q => (
                    <SelectItem key={q.quality_id} value={q.quality_id}>{q.quality_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Finish Quality</Label>
              <Select value={filters.subqualityId} onValueChange={(v) => setFilters({ ...filters, subqualityId: v })}>
                <SelectTrigger data-testid="filter-finish-quality" className="h-10">
                  <SelectValue placeholder="All Finish Qualities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Finish Qualities</SelectItem>
                  {subqualities.map(sq => (
                    <SelectItem key={sq.subquality_id} value={sq.subquality_id}>{sq.subquality_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Item</Label>
              <Select value={filters.item} onValueChange={(v) => setFilters({ ...filters, item: v })}>
                <SelectTrigger data-testid="filter-item" className="h-10">
                  <SelectValue placeholder="All Items" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  {uniqueItems.map(item => (
                    <SelectItem key={item} value={item}>{item}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger data-testid="filter-status" className="h-10">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Partially Billed">Partially Billed</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={clearFilters} className="w-full h-10">Clear Filters</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Package className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Orders</p>
                <p className="text-2xl font-bold text-slate-900">{summary.totalOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-50 rounded-lg">
                <TrendingUp className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Value</p>
                <p className="text-2xl font-bold text-slate-900">₹{formatNumber(summary.totalValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-lg">
                <BarChart3 className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-slate-900">{summary.pendingOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-bold text-slate-900">{summary.completedOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Tabs */}
      <Tabs defaultValue="orders" className="w-full">
        <TabsList className="grid w-full grid-cols-6 mb-4">
          <TabsTrigger value="orders" data-testid="orders-report-tab">Orders</TabsTrigger>
          <TabsTrigger value="lr-wise" data-testid="lr-wise-report-tab">LR Wise</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="suppliers-report-tab">Suppliers</TabsTrigger>
          <TabsTrigger value="qualities" data-testid="qualities-report-tab">Qualities</TabsTrigger>
          <TabsTrigger value="finishqualities" data-testid="finish-qualities-report-tab">Finish Quality</TabsTrigger>
          <TabsTrigger value="items" data-testid="items-report-tab">Items</TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle>Orders Report</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Quality</TableHead>
                      <TableHead>Finish Quality</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Ordered</TableHead>
                      <TableHead className="text-right">Received</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.slice(0, 50).map((order) => (
                      <TableRow key={order.order_id}>
                        <TableCell className="font-mono font-medium">{order.order_number}</TableCell>
                        <TableCell>{formatDate(order.order_date)}</TableCell>
                        <TableCell>{order.supplier_name}</TableCell>
                        <TableCell>{order.quality_name}</TableCell>
                        <TableCell>{order.subquality_name || '-'}</TableCell>
                        <TableCell>{order.item || '-'}</TableCell>
                        <TableCell className="text-right">{formatNumber(order.ordered_quantity)} {order.order_unit}</TableCell>
                        <TableCell className="text-right">{formatNumber(order.total_received_quantity)} {order.order_unit}</TableCell>
                        <TableCell className="text-right">₹{formatNumber(order.order_amount || 0)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            order.order_status === 'Completed' ? 'bg-green-50 text-green-700' :
                            order.order_status === 'Pending' ? 'bg-red-50 text-red-700' :
                            'bg-amber-50 text-amber-700'
                          }>
                            {order.order_status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredOrders.length > 50 && (
                  <p className="text-sm text-slate-500 mt-4 text-center">
                    Showing 50 of {filteredOrders.length} orders. Export to Excel/PDF for full data.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lr-wise">
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  LR Wise Report
                </CardTitle>
                <Badge variant="outline" className="text-sm">{lrData.length} LRs</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>LR #</TableHead>
                      <TableHead>Transport LR</TableHead>
                      <TableHead>LR Date</TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead>Bill</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Quality</TableHead>
                      <TableHead>Transport</TableHead>
                      <TableHead className="text-right">Bales</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Gate Pass</TableHead>
                      <TableHead>Receipt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lrData.length > 0 ? (
                      lrData.map((lr) => (
                        <TableRow key={lr.lr_id} data-testid={`lr-row-${lr.lr_id}`}>
                          <TableCell className="font-mono font-medium">{lr.lr_number}</TableCell>
                          <TableCell className="text-sm">{lr.lr_number_transport || '-'}</TableCell>
                          <TableCell>{formatDate(lr.lr_date)}</TableCell>
                          <TableCell className="font-mono">{lr.order_number}</TableCell>
                          <TableCell className="font-mono">{lr.bill_number || '-'}</TableCell>
                          <TableCell>{lr.supplier_name}</TableCell>
                          <TableCell>{lr.quality_name}</TableCell>
                          <TableCell>{lr.transport_name}</TableCell>
                          <TableCell className="text-right font-medium">{lr.bales_in_lr}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              lr.status === 'Delivered' ? 'bg-green-50 text-green-700' :
                              lr.status === 'Issued for Delivery' ? 'bg-blue-50 text-blue-700' :
                              'bg-amber-50 text-amber-700'
                            }>
                              {lr.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{lr.gate_pass_no || '-'}</TableCell>
                          <TableCell className="text-sm">{lr.receipt_number || '-'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center py-12 text-slate-500">
                          <Truck className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                          <p>No LR data found</p>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suppliers">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle>Supplier-wise Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                      <TableHead className="text-right">Total Ordered</TableHead>
                      <TableHead className="text-right">Total Received</TableHead>
                      <TableHead className="text-right">Total Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {supplierSummary.map((supplier) => (
                      <TableRow key={supplier.name}>
                        <TableCell className="font-medium">{supplier.name}</TableCell>
                        <TableCell>{supplier.city}</TableCell>
                        <TableCell className="text-right">{supplier.orderCount}</TableCell>
                        <TableCell className="text-right">{formatNumber(supplier.totalOrdered)}</TableCell>
                        <TableCell className="text-right">{formatNumber(supplier.totalReceived)}</TableCell>
                        <TableCell className="text-right font-medium">₹{formatNumber(supplier.totalValue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="qualities">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle>Quality-wise Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quality</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                      <TableHead className="text-right">Total Ordered</TableHead>
                      <TableHead className="text-right">Total Received</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {qualitySummary.map((quality) => (
                      <TableRow key={quality.name}>
                        <TableCell className="font-medium">{quality.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-teal-50 text-teal-700">{quality.unit}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{quality.orderCount}</TableCell>
                        <TableCell className="text-right">{formatNumber(quality.totalOrdered)}</TableCell>
                        <TableCell className="text-right">{formatNumber(quality.totalReceived)}</TableCell>
                        <TableCell className="text-right">{formatNumber(quality.totalOrdered - quality.totalReceived)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="finishqualities">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle>Finish Quality-wise Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Finish Quality</TableHead>
                      <TableHead>Parent Quality</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                      <TableHead className="text-right">Total Ordered</TableHead>
                      <TableHead className="text-right">Total Received</TableHead>
                      <TableHead className="text-right">Total Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {finishQualitySummary.map((sq) => (
                      <TableRow key={sq.name}>
                        <TableCell className="font-medium">{sq.name}</TableCell>
                        <TableCell>{sq.qualityName}</TableCell>
                        <TableCell className="text-right">{sq.orderCount}</TableCell>
                        <TableCell className="text-right">{formatNumber(sq.totalOrdered)}</TableCell>
                        <TableCell className="text-right">{formatNumber(sq.totalReceived)}</TableCell>
                        <TableCell className="text-right font-medium">₹{formatNumber(sq.totalValue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="items">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle>Item-wise Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                      <TableHead className="text-right">Total Ordered</TableHead>
                      <TableHead className="text-right">Total Received</TableHead>
                      <TableHead className="text-right">Total Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itemSummary.map((item) => (
                      <TableRow key={item.name}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-right">{item.orderCount}</TableCell>
                        <TableCell className="text-right">{formatNumber(item.totalOrdered)}</TableCell>
                        <TableCell className="text-right">{formatNumber(item.totalReceived)}</TableCell>
                        <TableCell className="text-right font-medium">₹{formatNumber(item.totalValue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
